import {
  AUTOSAVE_DEBOUNCE_MS,
  compactDiagramVersions,
  createEmptyDiagram,
  createNode,
  pushDiagramVersion,
  sanitizeDiagramStrings,
  normalizeNetworkLifecycle,
  type DiagramDocument,
  type DiagramEdge,
  type DiagramNode,
  type DiagramVersionRecord,
  type DeviceType,
  type NetworkLifecycle,
} from '@ukrop-nm/shared';
import { persistDiagramLocal } from './localDiagramPersist';
import { importDrawioBytes as parseDrawioBytes, importDrawioFile, type DrawioImportResult } from '@ukrop-nm/drawio-importer';
import officeEtalon from '../../../../samples/diagram-office-3floor-etalon.json';
import { allocateSiteId, isBuiltinSite, listCustomSites, removeCustomSite, upsertCustomSite } from './siteIndex';
import { parseImportedDiagram } from './parseImportedDiagram';
import { applyCsvRegistries } from './csvRegistries';
import { accountStorageKey } from '../auth/accountScope';

type Listener = () => void;

function storageKey(siteId: string) {
  return accountStorageKey(`diagram.${siteId}`);
}
function versionsKey(siteId: string) {
  return accountStorageKey(`diagram.versions.${siteId}`);
}

/** Cloud write is explicit (ribbon Save / logout). Autosave stays in localStorage. */
export type CloudPersistOpts = { immediate?: boolean; localOnly?: boolean };

let persistHandler: ((opts?: CloudPersistOpts) => void) | null = null;

function createOfficeDiagram(): DiagramDocument {
  const doc = structuredClone(officeEtalon) as DiagramDocument;
  doc.siteId = 'SITE-OFFICE-3F';
  doc.name = 'Триповерховий офіс · показова схема';
  doc.lifecycle = normalizeNetworkLifecycle(doc.lifecycle) || 'draft';
  return doc;
}

function defaultDiagram(siteId: string): DiagramDocument {
  if (siteId === 'SITE-OFFICE-3F') return createOfficeDiagram();
  const site = listCustomSites().find((s) => s.id === siteId);
  const name =
    site?.title && site.title !== siteId ? site.title : 'Нова схема';
  return createEmptyDiagram(siteId, name, 'draft');
}

function loadDiagram(siteId: string): DiagramDocument {
  try {
    const raw = localStorage.getItem(storageKey(siteId));
    if (raw) {
      const doc = JSON.parse(raw) as DiagramDocument;
      doc.lifecycle = normalizeNetworkLifecycle(doc.lifecycle);
      const site = listCustomSites().find((s) => s.id === siteId);
      const title = site?.title && site.title !== siteId ? site.title : null;
      if ((!doc.name?.trim() || doc.name === siteId) && title) {
        doc.name = title;
      }
      return doc;
    }
  } catch {
    // ignore
  }
  return defaultDiagram(siteId);
}

function loadVersions(siteId: string): DiagramVersionRecord[] {
  try {
    const raw = localStorage.getItem(versionsKey(siteId));
    if (raw) {
      const parsed = JSON.parse(raw) as DiagramVersionRecord[];
      if (Array.isArray(parsed)) return compactDiagramVersions(parsed);
    }
  } catch {
    // ignore
  }
  return [];
}

class DiagramStore {
  private siteId = 'SITE-OFFICE-3F';
  private diagram: DiagramDocument = loadDiagram(this.siteId);
  private versions: DiagramVersionRecord[] = loadVersions(this.siteId);
  private past: DiagramDocument[] = [];
  private future: DiagramDocument[] = [];
  private listeners = new Set<Listener>();
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSanitization: string[] = [];

  setPersistHandler(handler: ((opts?: CloudPersistOpts) => void) | null) {
    persistHandler = handler;
  }

  reloadFromStorage(opts?: { force?: boolean }) {
    const loaded = loadDiagram(this.siteId);
    const live = this.diagram;
    const liveTs = live.siteId === this.siteId ? live.updatedAt || '' : '';
    const loadedTs = loaded.updatedAt || '';
    if (!opts?.force && liveTs && (!loadedTs || liveTs >= loadedTs)) {
      this.versions = loadVersions(this.siteId);
      this.emit();
      return;
    }
    this.diagram = loaded;
    this.versions = loadVersions(this.siteId);
    this.past = [];
    this.future = [];
    this.lastSanitization = [];
    this.emit();
  }

  hasPendingSave() {
    return this.saveTimer != null;
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit() {
    for (const l of this.listeners) l();
  }

  getSiteId() {
    return this.siteId;
  }

  setSite(siteId: string) {
    if (siteId === this.siteId) return;
    this.siteId = siteId;
    this.diagram = loadDiagram(siteId);
    this.versions = loadVersions(siteId);
    this.past = [];
    this.future = [];
    this.lastSanitization = [];
    this.emit();
  }

  getSnapshot() {
    return this.diagram;
  }

  getVersions() {
    return this.versions;
  }

  getSanitizationReport() {
    return this.lastSanitization;
  }

  private pushHistory() {
    this.past.push(structuredClone(this.diagram));
    if (this.past.length > 100) this.past.shift();
    this.future = [];
  }

  private scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.saveExplicit(), AUTOSAVE_DEBOUNCE_MS);
  }

  replaceDiagram(next: DiagramDocument, opts?: { history?: boolean }) {
    if (opts?.history !== false) this.pushHistory();
    this.diagram = {
      ...next,
      siteId: this.siteId,
      lifecycle: normalizeNetworkLifecycle(next.lifecycle),
      updatedAt: new Date().toISOString(),
    };
    this.emit();
    this.scheduleAutosave();
  }

  importDrawioText(xml: string) {
    return this.applyDrawioImport(importDrawioFile(xml, this.siteId));
  }

  importDrawioBytes(bytes: Uint8Array, filename: string) {
    return this.applyDrawioImport(parseDrawioBytes(bytes, filename, this.siteId));
  }

  private applyDrawioImport(result: DrawioImportResult) {
    const fatal = result.warnings.some((w) => w.code === 'NOT_DRAWIO' || w.code === 'PARSE_ERROR');
    if (fatal && result.document.nodes.length === 0) {
      return result.warnings;
    }
    const { document, findings } = sanitizeDiagramStrings(result.document);
    this.lastSanitization = findings.map((f) => `${f.kind}: ${f.value} @ ${f.path}`);
    this.replaceDiagram(document);
    return result.warnings;
  }

  importCsvFiles(files: Array<{ name: string; text: string }>) {
    const result = applyCsvRegistries(this.diagram, files);
    const { document, findings } = sanitizeDiagramStrings(result.document);
    this.lastSanitization = findings.map((f) => `${f.kind}: ${f.value} @ ${f.path}`);
    this.replaceDiagram(document);
    return result.warnings;
  }

  importJsonText(text: string) {
    const incoming = parseImportedDiagram(text);
    const { document, findings } = sanitizeDiagramStrings(incoming);
    this.lastSanitization = findings.map((f) => `${f.kind}: ${f.value} @ ${f.path}`);
    this.replaceDiagram({
      ...document,
      siteId: this.siteId,
      id: document.id || `diagram-${this.siteId}`,
    });
    return this.lastSanitization;
  }

  updateNodes(updater: (nodes: DiagramNode[]) => DiagramNode[], history = true) {
    if (history) this.pushHistory();
    this.diagram = {
      ...this.diagram,
      nodes: updater(this.diagram.nodes),
      updatedAt: new Date().toISOString(),
    };
    this.emit();
    this.scheduleAutosave();
  }

  updateEdges(updater: (edges: DiagramEdge[]) => DiagramEdge[], history = true) {
    if (history) this.pushHistory();
    this.diagram = {
      ...this.diagram,
      edges: updater(this.diagram.edges),
      updatedAt: new Date().toISOString(),
    };
    this.emit();
    this.scheduleAutosave();
  }

  addDevice(type: DeviceType, position: { x: number; y: number }) {
    const node = createNode(type, position, crypto.randomUUID());
    this.updateNodes((nodes) => [...nodes, node]);
  }

  undo() {
    const prev = this.past.pop();
    if (!prev) return;
    this.future.push(structuredClone(this.diagram));
    this.diagram = prev;
    this.emit();
    this.scheduleAutosave();
  }

  redo() {
    const next = this.future.pop();
    if (!next) return;
    this.past.push(structuredClone(this.diagram));
    this.diagram = next;
    this.emit();
    this.scheduleAutosave();
  }

  saveExplicit() {
    const { document, findings } = sanitizeDiagramStrings(this.diagram);
    this.diagram = document;
    this.lastSanitization = findings.map((f) => `${f.kind}: ${f.value} @ ${f.path}`);
    this.versions = pushDiagramVersion(this.versions, this.diagram);
    this.writeLocal();
    this.syncCustomSiteIndex();
    persistHandler?.({ localOnly: true });
    this.emit();
  }

  flushSave() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    const { document, findings } = sanitizeDiagramStrings(this.diagram);
    this.diagram = document;
    this.lastSanitization = findings.map((f) => `${f.kind}: ${f.value} @ ${f.path}`);
    this.versions = pushDiagramVersion(this.versions, this.diagram);
    this.writeLocal();
    this.syncCustomSiteIndex();
    this.emit();
    const persist = persistHandler;
    if (persist) window.setTimeout(() => persist({ immediate: true }), 0);
  }

  private writeLocal() {
    persistDiagramLocal({
      diagramKey: storageKey(this.siteId),
      versionsKey: versionsKey(this.siteId),
      document: this.diagram,
      versions: this.versions,
    });
  }

  setName(name: string) {
    const next = name.trim();
    if (!next || this.diagram.name === next) return;
    this.pushHistory();
    this.diagram = {
      ...this.diagram,
      name: next,
      updatedAt: new Date().toISOString(),
    };
    this.emit();
    this.scheduleAutosave();
  }

  /** Name + lifecycle without emit/autosave — caller must flushSave. */
  applySaveFields(name: string, lifecycle: NetworkLifecycle) {
    const nextName = name.trim();
    const nextLife = normalizeNetworkLifecycle(lifecycle);
    const nameChanged = Boolean(nextName) && nextName !== this.diagram.name;
    const lifeChanged = nextLife !== this.diagram.lifecycle;
    if (!nameChanged && !lifeChanged) return;
    this.pushHistory();
    this.diagram = {
      ...this.diagram,
      name: nameChanged ? nextName : this.diagram.name,
      lifecycle: nextLife,
      updatedAt: new Date().toISOString(),
    };
  }

  setLifecycle(lifecycle: NetworkLifecycle) {
    const next = normalizeNetworkLifecycle(lifecycle);
    if (this.diagram.lifecycle === next) return;
    this.pushHistory();
    this.diagram = {
      ...this.diagram,
      lifecycle: next,
      updatedAt: new Date().toISOString(),
    };
    this.emit();
    this.scheduleAutosave();
  }

  private syncCustomSiteIndex() {
    const existing = listCustomSites().find((s) => s.id === this.siteId);
    if (!existing) return;
    const rawName = this.diagram.name?.trim();
    const title =
      rawName && rawName !== this.siteId
        ? rawName
        : existing.title && existing.title !== this.siteId
          ? existing.title
          : rawName || existing.title;
    if (
      (!this.diagram.name?.trim() || this.diagram.name === this.siteId) &&
      title &&
      title !== this.siteId
    ) {
      this.diagram = { ...this.diagram, name: title };
    }
    try {
      upsertCustomSite({
        ...existing,
        title,
        lifecycle: normalizeNetworkLifecycle(this.diagram.lifecycle ?? existing.lifecycle),
      });
    } catch {
      // site-index write may hit quota; cloud persist still runs
    }
  }

  restoreVersion(id: string) {
    const version = this.versions.find((v) => v.id === id);
    if (!version) return;
    this.replaceDiagram(version.document);
    this.saveExplicit();
  }

  deleteSelected(ids: string[]) {
    const idSet = new Set(ids);
    this.pushHistory();
    this.diagram = {
      ...this.diagram,
      nodes: this.diagram.nodes.filter((n) => !idSet.has(n.id) || n.locked),
      edges: this.diagram.edges.filter(
        (e) => !idSet.has(e.id) && !idSet.has(e.sourceNodeId) && !idSet.has(e.targetNodeId),
      ),
      updatedAt: new Date().toISOString(),
    };
    this.emit();
    this.scheduleAutosave();
  }

  duplicateSelected(ids: string[]) {
    const idSet = new Set(ids);
    const clones: DiagramNode[] = [];
    for (const node of this.diagram.nodes) {
      if (!idSet.has(node.id) || node.type === 'zone') continue;
      const newId = crypto.randomUUID();
      clones.push({
        ...structuredClone(node),
        id: newId,
        position: { x: node.position.x + 24, y: node.position.y + 24 },
        monitorKey: `${node.monitorKey}-COPY`,
        label: `${node.label} (копія)`,
      });
    }
    if (!clones.length) return;
    this.updateNodes((nodes) => [...nodes, ...clones]);
  }

  alignSelected(ids: string[], mode: 'left' | 'top') {
    const selected = this.diagram.nodes.filter((n) => ids.includes(n.id));
    if (selected.length < 2) return;
    const value =
      mode === 'left'
        ? Math.min(...selected.map((n) => n.position.x))
        : Math.min(...selected.map((n) => n.position.y));
    this.updateNodes((nodes) =>
      nodes.map((n) =>
        ids.includes(n.id)
          ? {
              ...n,
              position:
                mode === 'left' ? { ...n.position, x: value } : { ...n.position, y: value },
            }
          : n,
      ),
    );
  }

  resetToDefault() {
    this.replaceDiagram(defaultDiagram(this.siteId));
    this.saveExplicit();
  }

  /** Clears local diagram + versions for a site. Builtin templates stay as factory defaults. */
  clearSiteLocalData(siteId: string) {
    localStorage.removeItem(storageKey(siteId));
    localStorage.removeItem(versionsKey(siteId));
    if (!isBuiltinSite(siteId)) {
      removeCustomSite(siteId);
    }
    if (this.siteId === siteId) {
      this.diagram = defaultDiagram(siteId);
      this.versions = [];
      this.past = [];
      this.future = [];
      this.lastSanitization = [];
      this.emit();
    }
    persistHandler?.({ localOnly: true });
  }

  createPlannedSite(title: string): string {
    const name = title.trim() || 'Нова схема';
    const id = allocateSiteId(name);
    const doc = createEmptyDiagram(id, name, 'draft');
    upsertCustomSite({
      id,
      title: name,
      createdAt: new Date().toISOString(),
      lifecycle: 'draft',
    });
    localStorage.setItem(storageKey(id), JSON.stringify(doc));
    persistHandler?.({ localOnly: true });
    this.setSite(id);
    return id;
  }
}

export const diagramStore = new DiagramStore();
